import { ReactNode } from 'react'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useNavigate } from 'react-router-dom'
import { useConfigUI } from '@/features/config/context'
import useDecoderUsage from '@/features/config/hooks/controller/useDecoderUsage'
import useDeleteLineUnit from '@/features/config/hooks/controller/useDeleteLineUnit'
import { getApiErrorMessage } from '@/helpers'
import useTranslation from '@/hooks/useTranslation'
import { DecoderUsageItem, LU_PROTOCOL_LABEL, LuProtocol } from '@/types/controller'
import { usageLink, usageRoleKey, usageTypeKey } from './decoderUsage'
import { controllerTokens } from './uiTokens'

interface DeleteDecoderDialogProps {
  open: boolean
  onClose: () => void
  onDone: () => void
  onDeleted?: () => void
  interfaceId: number
  decoder: { id?: string | number; address?: string | number; index?: string | number }
  protocol: number
  isDark?: boolean
}

const DeleteDecoderDialog = ({
  open,
  onClose,
  onDone,
  onDeleted,
  interfaceId,
  decoder,
  protocol,
  isDark,
}: DeleteDecoderDialogProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { openSidebar, setSidebarView } = useConfigUI()
  const c = controllerTokens(isDark)

  const link = isDark ? '#58A6FF' : '#0969DA'
  const dangerSoft = isDark ? 'rgba(255,107,107,0.12)' : 'rgba(192,57,43,0.07)'
  const dangerLine = isDark ? 'rgba(255,107,107,0.35)' : 'rgba(192,57,43,0.22)'
  const warnSoft = isDark ? 'rgba(238,205,29,0.10)' : 'rgba(181,84,10,0.07)'

  const id = decoder?.id ? String(decoder.id) : undefined
  const {
    data: usage,
    isFetching,
    isError,
    error,
    refetch,
  } = useDecoderUsage(interfaceId, id, open)
  const { mutate: deleteDecoder, isLoading: deleting } = useDeleteLineUnit()

  const usages = usage?.usages ?? []
  const inUse = !!usage?.inUse || usages.length > 0
  const isOn = !!usage?.isOn
  const ready = !!usage && !isFetching

  const frontendType = LU_PROTOCOL_LABEL[protocol]
    ? LU_PROTOCOL_LABEL[protocol].replace(/^src\s+/i, '').toUpperCase()
    : undefined
  const type = frontendType || t('commonWordUnknown')
  const isMk3 = protocol === LuProtocol.Mk3
  const address = usage?.address || decoder?.address
  const indexes = usage?.outputs?.length
    ? usage.outputs.map((output) => output.index)
    : [decoder?.index]

  const label = (item: DecoderUsageItem) => {
    const typeKey = usageTypeKey(item)
    const name = item.name || t(typeKey ?? 'commonWordUnknown')
    const roleKey = usageRoleKey(item)
    const role = roleKey ? t(roleKey) : ''
    const showRole = role && !name.toLowerCase().includes(role.toLowerCase())
    return [
      name,
      showRole ? `– ${role}` : '',
      item.outputIndex !== undefined
        ? `(${t('decoderUsageOutput', { index: item.outputIndex })})`
        : '',
    ]
      .filter(Boolean)
      .join(' ')
  }

  const openUsage = (item: DecoderUsageItem) => {
    const target = usageLink(item)
    if (!target) return
    onClose()
    onDone()
    if (target.kind === 'sidebar') {
      openSidebar()
      setSidebarView(target.view)
    } else {
      navigate(target.to, { state: target.state })
    }
  }

  const handleDelete = () => {
    if (!id) return
    deleteDecoder(
      { interfaceId, id },
      {
        onSuccess: () => {
          onDeleted?.()
          onClose()
          onDone()
        },
      },
    )
  }

  const close = () => !deleting && onClose()

  return (
    <Dialog
      open={open}
      onClose={close}
      sx={{
        '& .MuiDialog-paper': {
          width: 560,
          maxWidth: 'calc(100% - 32px)',
          borderRadius: '16px',
          backgroundColor: c.card,
          backgroundImage: 'none',
        },
      }}
    >
      <Box sx={{ p: '28px 28px 24px', position: 'relative' }}>
        <IconButton
          aria-label={t('commonActionsCancel')}
          onClick={close}
          sx={{ position: 'absolute', top: 16, right: 16, color: c.muted }}
        >
          <CloseIcon />
        </IconButton>

        {/* Header */}
        <Stack direction="row" spacing="18px" alignItems="center" sx={{ pr: '40px' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              flexShrink: 0,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: c.danger,
              backgroundColor: dangerSoft,
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 30 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '22px', fontWeight: 700, color: c.textHi, lineHeight: 1.2 }}>
              {t('decoderDeleteTitle')}
            </Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: 600, color: c.text, mt: '4px' }}>
              {t('decoderDeleteSummary', { address, type })}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: c.muted, mt: '2px' }}>
              {t('decoderDeleteOutputs', { indexes: indexes.join(', ') })}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ mt: '22px' }}>
          {isFetching && (
            <Stack alignItems="center" sx={{ py: '24px' }}>
              <CircularProgress size={22} sx={{ color: c.accent }} />
            </Stack>
          )}

          {!isFetching && isError && (
            <Notice
              icon={<ErrorRoundedIcon sx={{ fontSize: 28, color: c.danger }} />}
              bg={dangerSoft}
              border={dangerLine}
            >
              <Typography sx={{ fontSize: '13px', color: c.text }}>
                {getApiErrorMessage(error, t('decoderDeleteUsageLoadFailed'))}
              </Typography>
              <Typography
                onClick={() => refetch()}
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: link,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  mt: '4px',
                }}
              >
                {t('tryAgain')}
              </Typography>
            </Notice>
          )}

          {ready && isOn && (
            <Box sx={{ mb: inUse ? '14px' : 0 }}>
              <Notice
                icon={<ErrorRoundedIcon sx={{ fontSize: 30, color: c.danger }} />}
                bg={dangerSoft}
                border={dangerLine}
              >
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: c.danger }}>
                  {t('decoderDeleteIsOn')}
                </Typography>
              </Notice>
            </Box>
          )}

          {ready && inUse && (
            <>
              <Notice
                icon={<ErrorRoundedIcon sx={{ fontSize: 30, color: c.danger }} />}
                bg={dangerSoft}
                border={dangerLine}
              >
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: c.danger }}>
                  {t('decoderDeleteInUseMessage')}
                </Typography>
                <Typography sx={{ fontSize: '13px', color: c.text, mt: '2px' }}>
                  {t('decoderDeleteInUseHint')}
                </Typography>
              </Notice>

              <Box sx={{ mt: '14px', p: '14px', borderRadius: '12px', backgroundColor: c.page }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: c.textHi, mb: '10px' }}>
                  {t('decoderDeleteInUseTitle')}
                </Typography>
                <Stack spacing="8px" sx={{ maxHeight: '260px', overflowY: 'auto' }}>
                  {usages.map((item, n) => {
                    const clickable = !!usageLink(item)
                    return (
                      <Box
                        key={`${item.type}-${item.objectId}-${n}`}
                        role={clickable ? 'button' : undefined}
                        onClick={clickable ? () => openUsage(item) : undefined}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '14px',
                          p: '10px 14px 10px 10px',
                          borderRadius: '10px',
                          backgroundColor: c.card,
                          border: `1px solid ${c.line}`,
                          cursor: clickable ? 'pointer' : 'default',
                          transition: 'background-color .15s ease',
                          '&:hover': clickable ? { backgroundColor: c.hover } : undefined,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: '14px',
                              fontWeight: 500,
                              color: clickable ? link : c.text,
                              textDecoration: clickable ? 'underline' : 'none',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {label(item)}
                          </Typography>
                          <Typography sx={{ fontSize: '12px', color: c.muted, mt: '2px' }}>
                            {usageTypeKey(item)
                              ? t('decoderUsageHint', {
                                  type: t(usageTypeKey(item) as string).toLowerCase(),
                                })
                              : t('decoderUsageHintGeneric')}
                          </Typography>
                        </Box>
                        {clickable && <ChevronRightIcon sx={{ color: c.muted }} />}
                      </Box>
                    )
                  })}
                </Stack>
              </Box>
            </>
          )}

          {ready && !inUse && !isOn && (
            <Notice
              icon={<InfoOutlinedIcon sx={{ fontSize: 28, color: c.warn }} />}
              bg={warnSoft}
              border={`${c.warn}55`}
            >
              <Typography sx={{ fontSize: '14px', color: c.text }}>
                {t('decoderDeleteConfirm', { address, type })}
              </Typography>
              {isMk3 && (
                <Typography sx={{ fontSize: '12.5px', color: c.muted, mt: '6px' }}>
                  {t('decoderDeleteMk3Note')}
                </Typography>
              )}
            </Notice>
          )}
        </Box>

        {/* Actions */}
        <Stack direction="row" justifyContent="flex-end" spacing="12px" sx={{ mt: '24px' }}>
          <Button
            variant="secondary"
            disabled={deleting}
            onClick={onClose}
            sx={{
              minWidth: '120px',
              height: '40px',
              borderRadius: '8px',
              fontSize: '14px',
              color: c.text,
              backgroundColor: c.card,
              border: `1px solid ${c.line}`,
              '&:hover': { backgroundColor: c.hover },
            }}
          >
            {t('commonActionsCancel')}
          </Button>
          <LoadingButton
            loading={deleting}
            variant="primary"
            disabled={!ready || inUse || isOn}
            onClick={handleDelete}
            sx={{
              minWidth: '120px',
              height: '40px',
              borderRadius: '8px',
              fontSize: '14px',
              '&.Mui-disabled': {
                color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.85)',
                backgroundColor: isDark ? '#2b4552' : '#a3b8b7',
              },
            }}
          >
            {t('commonActionsDelete')}
          </LoadingButton>
        </Stack>
      </Box>
    </Dialog>
  )
}

const Notice = ({
  icon,
  bg,
  border,
  children,
}: {
  icon: ReactNode
  bg: string
  border: string
  children: ReactNode
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      p: '14px 16px',
      borderRadius: '12px',
      backgroundColor: bg,
      border: `1px solid ${border}`,
    }}
  >
    <Box sx={{ flexShrink: 0, display: 'flex' }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
)

export default DeleteDecoderDialog
